import sortBy_ from 'lodash/sortBy';

import {createSelector} from 'reselect';

import {createNestedNS, getPath} from '../../../../shared/utils/settings';
import {FAVOURITES, NAMESPACES, SettingName} from '../../../../shared/constants/settings';

import {DEFAULT_PATH} from '../../../constants/navigation';

function createGetSetting(settings) {
    return (name, parentNS) => settings.data[getPath(name, parentNS)];
}

const selectSettings = (state) => state.settings;

export const selectGetSetting = createSelector(selectSettings, createGetSetting);

const selectCluster = (state) => state.global.cluster;

export const selectClusterNS = createSelector(selectCluster, (cluster) =>
    createNestedNS(cluster, NAMESPACES.LOCAL),
);

export const selectSchedulingNS = createSelector(selectClusterNS, (clusterNS) =>
    createNestedNS('scheduling', clusterNS),
);

export const selectAccountsNS = createSelector(selectClusterNS, (clusterNS) =>
    createNestedNS('accounts', clusterNS),
);

export const selectBundlesNS = createSelector(selectClusterNS, (clusterNS) =>
    createNestedNS('bundles', clusterNS),
);

export const selectChaosBundlesNS = createSelector(selectClusterNS, (clusterNS) =>
    createNestedNS('chaosBundles', clusterNS),
);

export const selectChytNS = createSelector(selectClusterNS, (clusterNS) => {
    return createNestedNS('chyt', clusterNS);
});

export const selectSettingTheme = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.GLOBAL.THEME, NAMESPACES.GLOBAL),
);

export const selectLastVisitedTabs = createSelector(
    [selectGetSetting, selectClusterNS],
    (getSetting, clusterNS) => getSetting(SettingName.LOCAL.LAST_VISITED_TAB, clusterNS) || {},
);

// [{ path: <path>, count: <count> }]
export const selectLastVisited = createSelector(
    [selectGetSetting, selectClusterNS],
    (getSetting, clusterNS) => getSetting(SettingName.LOCAL.LAST_VISITED, clusterNS) || [],
);

export const selectPopular = createSelector(
    selectLastVisited,
    // negate count to sort in descending order and make most visited entries come first
    (lastVisited) => sortBy_(lastVisited, (entry) => -entry.count),
);

export const selectFavourites = createSelector(
    [selectGetSetting, selectClusterNS],
    (getSetting, clusterNS) => getSetting(FAVOURITES, clusterNS) || [],
);

export const selectNavigationDefaultPath = createSelector(
    [selectGetSetting, selectClusterNS],
    (getSetting, clusterNS) =>
        getSetting(SettingName.LOCAL.NAVIGATION_DEFAULT_PATH, clusterNS) || DEFAULT_PATH,
);

export const selectIsPathAutoCorrectionSettingEnabled = createSelector(
    selectGetSetting,
    (getSetting) =>
        getSetting(SettingName.NAVIGATION.ENABLE_PATH_AUTO_CORRECTION, NAMESPACES.NAVIGATION),
);

export const selectIsRecentClustersFirst = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.MENU.RECENT_CLUSTER_FIRST, NAMESPACES.MENU),
);

export const selectIsRecentPagesFirst = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.MENU.RECENT_PAGE_FIRST, NAMESPACES.MENU),
);

export const selectShouldUseSafeColors = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.A11Y.USE_SAFE_COLORS, NAMESPACES.A11Y),
);

export const selectStartingPage = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.MENU.STARTING_PAGE, NAMESPACES.MENU),
);

export const selectShouldUsePreserveState = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.MENU.PRESERVE_STATE, NAMESPACES.MENU),
);

export const selectTemplates = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.COMPONENTS.TEMPLATES, NAMESPACES.COMPONENTS),
);

/** @type {(state: import('../reducers').RootState) => string[]} */
export const selectSelectedColumns = createSelector(selectGetSetting, (getSetting) => {
    const selectedColumns = getSetting(
        SettingName.COMPONENTS.SELECTED_COLUMNS,
        NAMESPACES.COMPONENTS,
    );

    if (Array.isArray(selectedColumns) && !selectedColumns.includes('actions')) {
        return [...selectedColumns, 'actions'];
    }
    return selectedColumns;
});

export const selectFormat = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.YSON.FORMAT, NAMESPACES.YSON),
);

export const selectShouldCompact = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.YSON.COMPACT, NAMESPACES.YSON),
);

export const selectShouldShowDecoded = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.YSON.SHOW_DECODED, NAMESPACES.YSON),
);

export const selectShouldEscapeWhitespace = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.YSON.ESCAPE_WHITESPACES, NAMESPACES.YSON),
);

export const selectUseBinaryAsHex = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.YSON.BINARY_AS_HEX, NAMESPACES.YSON),
);

export const selectShowDecoded = createSelector([selectGetSetting], (getSetting) =>
    getSetting(SettingName.YSON.SHOW_DECODED, NAMESPACES.YSON),
);

export const selectRowsPerTablePage = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.ROWS_PER_TABLE_PAGE, NAMESPACES.NAVIGATION),
);

export const selectMaximumTableStringSize = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.MAXIMUM_TABLE_STRING_SIZE, NAMESPACES.NAVIGATION),
);

export const selectDefaultTableColumnLimit = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.DEFAULT_TABLE_COLUMN_LIMIT, NAMESPACES.NAVIGATION),
);

export const selectIsTableSimilarityEnabled = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.ENABLE_TABLE_SIMILARITY, NAMESPACES.NAVIGATION),
);

export const selectClusterPagePaneSizes = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.CLUSTER_PAGE_PANE_SIZES, NAMESPACES.NAVIGATION),
);

export const selectShouldGroupNodes = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.GROUP_NODES, NAMESPACES.NAVIGATION),
);

export const selectShouldUseSmartFilter = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.USE_SMART_FILTER, NAMESPACES.NAVIGATION),
);

export const selectShouldUseSmartSort = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.USE_SMART_SORT, NAMESPACES.NAVIGATION),
);

export const selectDefaultChytAlias = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.NAVIGATION.DEFAULT_CHYT_ALIAS, NAMESPACES.NAVIGATION),
);

export const selectMastersHostType = createSelector(selectGetSetting, (getSetting) =>
    getSetting(SettingName.SYSTEM.MASTERS_HOST_TYPE, NAMESPACES.SYSTEM),
);

export const selectAccountsVisibilityModeOfDashboard = createSelector(
    selectGetSetting,
    (getSetting) => {
        return getSetting(SettingName.ACCOUNTS.DASHBOARD_VISIBILITY_MODE, NAMESPACES.ACCOUNTS);
    },
);

export const selectAccountsVisibilityMode = createSelector(selectGetSetting, (getSetting) => {
    return getSetting(SettingName.ACCOUNTS.ACCOUNTS_VISIBILITY_MODE, NAMESPACES.ACCOUNTS);
});

export const selectSettingTableDisplayRawStrings = createSelector(
    selectGetSetting,
    (getSetting) => {
        return getSetting(SettingName.NAVIGATION.TABLE_DISPLAY_RAW_STRINGS, NAMESPACES.NAVIGATION);
    },
);

export const selectSettingAnnotationVisibility = createSelector(selectGetSetting, (getSetting) => {
    return getSetting(SettingName.NAVIGATION.ANNOTATION_VISIBILITY, NAMESPACES.NAVIGATION);
});

export const selectSettingNavigationPanelExpanded = createSelector(
    selectGetSetting,
    (getSetting) => {
        const res = getSetting(SettingName.GLOBAL.NAVIGATION_PANEL_EXPAND, NAMESPACES.GLOBAL);
        if (res !== undefined) {
            return res;
        }

        try {
            const data = localStorage['nvAsideHeader'];
            if (!data) {
                return false;
            }
            const {isCompact} = JSON.parse(data) || {};
            return !isCompact;
        } catch {
            return false;
        }
    },
);
